import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import { currency as money } from "../../lib/money";
import type { CompanyDetail } from "../../lib/crm-types";
import { cn } from "../ui/cn";
import { CreditReportDialog } from "./CreditReportDialog";
import { CreditsafeLinkDialog } from "./CreditsafeLinkDialog";

/**
 * The credit file, as a bureau reports it.
 *
 * Sits beside the AI summary rather than in Details, because it answers a
 * different question: Details is who they are, this is whether to extend them
 * terms. A rep about to quote for thirty thousand pounds wants shareholders'
 * funds and the credit limit on the same screen as the account, not in a tab
 * somebody has to remember exists.
 *
 * Nothing is inferred and nothing is computed. Every figure shown is one a
 * bureau supplied, dated with the period it covers -- a two-year-old set of
 * accounts read as current is exactly the mistake this panel could cause, so
 * the date is not optional decoration.
 *
 * The figures themselves come down on the company record, which is why they
 * render before the credit query resolves: they are a projection of the newest
 * ordered report, kept on `companies` so the list can sort on a rating. The
 * separate query is for everything *about* the file -- who linked it, when it
 * was ordered, whether it has gone stale -- and for the buttons, which is also
 * why a slow or absent bureau leaves the numbers on screen rather than blanking
 * them.
 */
export function FinancialsPanel({ company }: { company: CompanyDetail }) {
  const queryClient = useQueryClient();
  const [linking, setLinking] = useState(false);
  const [reading, setReading] = useState(false);

  const credit = useQuery({
    queryKey: queryKeys.crmCredit(company.id),
    queryFn: () => crmApi.credit(company.id),
    staleTime: 60_000,
  });

  const refresh = useMutation({
    mutationFn: () => crmApi.refreshCredit(company.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.crmCompany(company.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.crmCredit(company.id) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCreditHistory(company.id),
      });
      void queryClient.invalidateQueries({ queryKey: ["crm", "timeline", company.id] });
    },
  });

  const panel = credit.data;
  const linked = panel?.available === true ? panel.value : null;
  /* Undefined while the query is in flight: assumed connected, because the
     alternative is a Link button that appears a beat after the panel does. */
  const bureauConnected =
    panel === undefined || panel.available || panel.reason !== "not_configured";

  /*
   * The `companies` money columns carry no currency of their own -- they are
   * whole units, and the schema assumes pounds because every account on this
   * desk is registered in Britain. The linked report does know, so it is
   * preferred when it has loaded: a French company's turnover rendered with a
   * £ is a wrong number, not a cosmetic slip.
   */
  const currency = linked?.report?.credit.currency ?? "GBP";

  const rows: [string, string | null][] = [
    ["Credit rating", company.creditRating ?? null],
    ["Credit limit", money(company.creditLimit, currency)],
    ["Turnover", money(company.turnover, currency)],
    ["Shareholders' funds", money(company.shareholdersFunds, currency)],
    ["Employees", number(company.employees)],
    ["Status", company.companyStatus ?? null],
  ];

  const anything = rows.some(([, value]) => value !== null);

  return (
    <section className="rounded-lg border border-line">
      {linking ? (
        <CreditsafeLinkDialog company={company} onClose={() => setLinking(false)} />
      ) : null}

      {reading && linked ? (
        <CreditReportDialog
          companyId={company.id}
          companyName={company.name}
          credit={linked}
          onClose={() => setReading(false)}
        />
      ) : null}

      <header className="flex items-baseline gap-2 border-b border-line px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-faint">
          Financials
        </h2>
        {company.accountsDate ? (
          <span
            title="The period these figures cover — not when we last checked"
            className="text-[11px] text-faint"
          >
            to {company.accountsDate}
          </span>
        ) : null}

        {/*
          Only offered once we know a bureau exists. A Link button on a server
          with no Creditsafe credentials is a button whose only outcome is an
          error, and the empty state below already explains why it is missing.
        */}
        {bureauConnected ? (
          <button
            type="button"
            disabled={refresh.isPending}
            onClick={() => (linked ? refresh.mutate() : setLinking(true))}
            /* Says out loud which button spends money. Both actions order a
               report; only one of them looks like it might. */
            title={
              linked
                ? "Order a fresh credit report from Creditsafe — this is billed"
                : "Find this company at Creditsafe and pull their credit file"
            }
            className="ml-auto text-xs text-brand-600 hover:text-brand-700 disabled:opacity-50"
          >
            {refresh.isPending ? "Ordering…" : linked ? "Refresh" : "Link Creditsafe"}
          </button>
        ) : null}
      </header>

      {anything ? (
        <>
          <dl className="divide-y divide-line">
            {rows.map(([label, value]) => (
              <div key={label} className="flex gap-2 px-3 py-1.5 text-xs">
                <dt className="w-32 shrink-0 text-faint">{label}</dt>
                <dd
                  className={cn(
                    "min-w-0 flex-1 break-words tabular-nums",
                    value ? "text-content" : "text-faint",
                  )}
                >
                  {value ?? "—"}
                </dd>
              </div>
            ))}
          </dl>

          <Provenance company={company} linked={linked} onRead={() => setReading(true)} />
        </>
      ) : (
        /*
         * Said plainly rather than shown as six dashes. An empty table looks
         * like a company with no money; this is a company nobody has looked up.
         */
        <p className="px-3 py-3 text-[11px] text-faint">
          {!bureauConnected
            ? "No credit bureau is connected on this server, so credit files cannot be looked up."
            : linked
              ? "Linked to Creditsafe, but their report carries no figures. Normal for a sole trader, and for a company incorporated in the last year."
              : "No credit file yet. Link this company to Creditsafe to pull their rating, limit and filed accounts."}
        </p>
      )}

      {refresh.isError ? (
        <p className="border-t border-line px-3 py-1.5 text-[11px] text-sla-breached">
          {refresh.error instanceof ApiError
            ? refresh.error.message
            : "Could not order a credit report."}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Where these numbers came from and how old they are.
 *
 * The most important few lines in the panel. Six figures with no attribution
 * read as facts about the company; the same six with "Creditsafe, ordered in
 * March" read as what they are -- a snapshot somebody paid for on a particular
 * day, which may since have moved.
 */
function Provenance({
  company,
  linked,
  onRead,
}: {
  company: CompanyDetail;
  linked: { orderedAt: string | null; stale: boolean; orderCount: number } | null;
  onRead: () => void;
}) {
  /* Nothing to attribute and nothing to open. A linked company with no figures
     still gets this row, because its report is worth reading even when the
     headline numbers are empty. */
  if (!company.financialsSource && !linked) return null;

  return (
    <div className="flex flex-wrap items-baseline gap-x-2 border-t border-line px-3 py-1.5 text-[11px]">
      {company.financialsSource ? (
        <span className="text-faint">
          From {company.financialsSource}
          {company.financialsCheckedAt
            ? `, checked ${company.financialsCheckedAt.slice(0, 10)}`
            : ""}
          .
        </span>
      ) : null}

      {/*
        Stale is advisory and says so by being a nudge rather than a warning
        badge: nothing re-orders on its own, because an automatic refresh is a
        bill nobody authorised. The rep decides whether this account is worth
        another check.
      */}
      {linked?.stale ? (
        <span className="text-sla-warning">Worth re-checking.</span>
      ) : null}

      {linked ? (
        <button
          type="button"
          onClick={onRead}
          className="ml-auto text-brand-600 hover:text-brand-700"
          title="Read the stored report — this does not order a new one"
        >
          Full report
          {linked.orderCount > 1 ? ` (${linked.orderCount} checks)` : ""}
        </button>
      ) : null}
    </div>
  );
}

/** Whole units, grouped. Negative is real -- an insolvent balance sheet. */

function number(value: number | null | undefined): string | null {
  return typeof value === "number" && Number.isFinite(value)
    ? String(value)
    : null;
}
